import { z } from 'zod';
import { AnomalySeverity, ConflictType, SourceSystem } from '../enums.js';
import { IsoDateTime, UuidString } from '../primitives.js';

/**
 * SourceConflict — first-class запись противоречия между источниками (R-BE.2).
 *
 * Backend НЕ формирует freeform `diff_label`. FE композирует через i18n:
 *   `m.conflict_diff_label(diff_params)` где diff_params — structural (expected/actual/units).
 *
 * `resolution_notes` — user-input (оператор вводит при resolve), не backend UI text.
 */
// S-FIX-4: removed .strict() (BE list handler returns subset of fields);
// made entity_id/details/timestamps optional to match prod data shape.
export const SourceConflict = z.object({
  id: UuidString,
  entity_id: UuidString.optional(),
  run_id: UuidString.nullable().optional(),
  conflict_type: ConflictType,
  /** UI-alias для `conflict_type`. */
  type: ConflictType.optional(),
  subject_kind: z
    .enum([
      'item',
      'warehouse',
      'batch',
      'bom',
      'movement',
      'stock_balance',
      'other',
      // S-FIX-4: prod has these from S14 finance conflict detector.
      'counterparty',
      'invoice',
      'payment',
      'settlement',
    ])
    .optional(),
  subject_ref: z.string().optional(),
  /** UI-alias для `subject_ref`. */
  subject: z.string().optional(),
  sources: z.array(SourceSystem).optional(),
  source_a: SourceSystem.optional(),
  source_b: SourceSystem.optional(),
  severity: AnomalySeverity.optional(),
  authoritative: SourceSystem.nullable().optional(),
  details: z.record(z.unknown()).optional(),
  is_resolved: z.boolean().default(false),
  resolved_by: UuidString.nullable().optional(),
  resolved_at: IsoDateTime.nullable().optional(),
  resolution_notes: z.string().nullable().optional(),
  diff_params: z.record(z.unknown()).optional(),
  created_at: IsoDateTime.optional(),
  updated_at: IsoDateTime.optional(),
  // i18n invariant (R-BE.18 / CLAUDE.md #19): backend НЕ шлёт freeform `message`
  // и legacy `diff_label` — FE композирует текст через m.conflict_diff_label(diff_params).
  // Схема осознанно НЕ .strict() (S-FIX-4: subset/prod-extra поля стрипаются), но эти
  // два legacy-freeform ключа запрещены явно: присутствие с любым значением → parse throws.
  message: z.undefined().optional(),
  diff_label: z.undefined().optional(),
});
export type SourceConflict = z.infer<typeof SourceConflict>;
